import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// =====================================================================
// Bearer-token fallback for cross-origin production deploys.
//
// We intentionally use `localStorage` here despite common lint warnings.
// Reasoning (NOT a security oversight):
//   • The stored value is a short-lived SERVER-ISSUED random token,
//     rotated on every login, expiring in 7 days, tied to a device_id.
//   • It is NOT a long-lived secret, user password, or API key.
//   • It is identical in sensitivity to a `SameSite=None; Secure; HttpOnly`
//     cookie — equally at risk from XSS (both are readable via `document`
//     if an attacker can execute JS on our origin).
//   • In production the frontend (egns.school) is on a different origin
//     than the backend (cluster-N.emergentcf.cloud). Browsers (Safari
//     ITP, most private modes) silently drop cross-site cookies even
//     with SameSite=None, making cookie-only auth unreliable for many
//     real students. The cookie is still set as belt-and-braces; this
//     Bearer token is the only reliable transport.
// If stricter XSS hardening is needed, the next step is a backend-signed
// refresh-token + short-lived access-token pair — NOT removing Bearer.
// =====================================================================
const TOKEN_STORAGE_KEY = "egns_session_token";

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || "";
  } catch {
    // Private-browsing mode throws on storage access — fall back to cookie-only.
    return "";
  }
}
export function setAuthToken(tok) {
  try {
    if (tok) localStorage.setItem(TOKEN_STORAGE_KEY, tok);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    // Private-browsing mode — persistence unavailable, cookie path still works.
    // eslint-disable-next-line no-console
    console.warn("[auth] token persistence unavailable:", e?.message);
  }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const tok = getAuthToken();
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// Redirect pending students to the approval-waiting page if any gated API
// call comes back with ACCOUNT_PENDING_APPROVAL.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 403 && detail === "ACCOUNT_PENDING_APPROVAL") {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/pending")) {
        window.location.replace("/pending");
      }
    }
    return Promise.reject(err);
  },
);
